import { formatDate, formatRelative, max } from 'date-fns';
import { z } from 'zod';

import {
  type CourierDeliveryStatus,
  type CourierDeliveryStatusType,
  CourierId,
  CourierStatus,
  type DeliveryStatusTask,
  UnifiedCourierId,
  type VehicleType,
} from 'common/api/api-types';

export const getIdleTime = ({
  courierStatusUpdatedAt,
  deliveryStatusUpdatedAt,
}: {
  courierStatusUpdatedAt?: Date;
  deliveryStatusUpdatedAt: Date;
}) => max([courierStatusUpdatedAt ?? deliveryStatusUpdatedAt, deliveryStatusUpdatedAt]);

export const ignoreDeliveryStatusWhenCourierIsOffline = <
  T extends { status: CourierDeliveryStatusType },
>({
  courierStatus,
  deliveryStatus,
}: {
  courierStatus?: CourierStatus;
  deliveryStatus?: T;
}): undefined | T => {
  switch (courierStatus) {
    case 'online':
    case 'unknown':
    case undefined:
      return deliveryStatus;
    case 'offline':
  }
};

export const OnlineAndDeliveryStatus = z.enum([
  'offline',
  'online+drop_off',
  'online+idle',
  'online+pick_up',
]);

export type OnlineAndDeliveryStatus = z.infer<typeof OnlineAndDeliveryStatus>;

// ╭────────────────┬──────────────────────┬────────────────────────────╮
// │ courier status │ delivery status      │ online and delivery status │
// ├────────────────┼──────────────────────┼────────────────────────────┤
// │ online         │ drop_off             │ 🔵 online+drop_off         │
// │ online         │ idle                 │ 🟠 online+idle             │
// │ online         │ pick_up              │ 🟢 online+pick_up          │
// │ online         │ unknown              │ 🟠 online+idle             │
// │ online         │ undefined            │ 🟠 online+idle             │
// │ unknown        │ drop_off             │ 🔵 online+drop_off         │
// │ unknown        │ idle                 │ 🟠 online+idle             │
// │ unknown        │ pick_up              │ 🟢 online+pick_up          │
// │ unknown        │ unknown              │ 🟠 online+idle             │
// │ unknown        │ undefined            │ 🟠 online+idle             │
// │ undefined      │ drop_off             │ 🔵 online+drop_off         │
// │ undefined      │ idle                 │ 🟠 online+idle             │
// │ undefined      │ pick_up              │ 🟢 online+pick_up          │
// │ undefined      │ unknown              │ 🟠 online+idle             │
// │ undefined      │ undefined            │ ⚪️ offline                 │
// │ offline        │ undefined (drop_off) │ ⚪️ offline                 │
// │ offline        │ undefined (idle)     │ ⚪️ offline                 │
// │ offline        │ undefined (pick_up)  │ ⚪️ offline                 │
// │ offline        │ undefined (unknown)  │ ⚪️ offline                 │
// │ offline        │ undefined            │ ⚪️ offline                 │
// ╰────────────────┴──────────────────────┴────────────────────────────╯
export const getOnlineAndDeliveryStatus = ({
  courierStatus,
  deliveryStatus: rawDeliveryStatus,
}: {
  courierStatus?: CourierStatus;
  deliveryStatus?: CourierDeliveryStatusType;
}) => {
  const deliveryStatus = ignoreDeliveryStatusWhenCourierIsOffline({
    courierStatus,
    deliveryStatus: rawDeliveryStatus ? { status: rawDeliveryStatus } : undefined,
  });

  if (courierStatus !== 'offline') {
    switch (deliveryStatus?.status) {
      case 'pick_up':
        return 'online+pick_up';
      case 'drop_off':
        return 'online+drop_off';
      case 'idle':
      case 'unknown':
        return 'online+idle';
      case undefined:
        if (courierStatus === 'online' || courierStatus === 'unknown') {
          return 'online+idle';
        }
    }
  }

  return 'offline';
};

export const getCourierStatusLabel = ({
  courierStatus,
  deliveryStatus,
  setOffline,
}: {
  courierStatus?: CourierStatus;
  deliveryStatus?: CourierDeliveryStatusType;
  setOffline?: boolean;
}) => {
  if (setOffline) {
    return 'Set offline';
  }

  const onlineAndDeliveryStatus = getOnlineAndDeliveryStatus({
    courierStatus,
    deliveryStatus,
  });

  switch (onlineAndDeliveryStatus) {
    case 'online+pick_up':
    case 'online+drop_off':
      return 'On task';
    case 'online+idle':
      return 'Idle';
    case 'offline':
      return 'Offline';
  }
};

export const getDeliveryEstimatedTimeOfArrival = (deliveryStatus?: CourierDeliveryStatus) => {
  switch (deliveryStatus?.status) {
    case 'pick_up':
      return deliveryStatus?.tasks?.[0].pickUpTime;
    case 'drop_off':
      return deliveryStatus?.tasks?.[0].dropOffTime;
    case 'idle':
    case 'unknown':
      return deliveryStatus.updatedAt;
    default:
  }
  return;
};

export const getTaskEstimatedTimeOfArrival = (task?: DeliveryStatusTask) => {
  switch (task?.status) {
    case 'pick_up':
      return task.pickUpTime;
    case 'drop_off':
      return task.dropOffTime;
    case 'idle':
    case 'unknown':
      return task.updatedAt;
    default:
  }
  return;
};

export const getVehicleType = (value?: VehicleType) => {
  switch (value) {
    case 'BICYCLE':
      return 'Bicycle';
    case 'CAR':
      return 'Car';
    case 'MOTORCYCLE':
      return 'Motorcycle';
    case 'ECAR':
      return 'ECar';
    case 'FLYING_DRONE':
      return 'Flying drone';
    case 'EBICYCLE':
      return 'EBicycle';
    case 'VAN':
      return 'Van';
    case 'EMOTORCYCLE':
      return 'EMotorcycle';
    case 'ESCOOTER':
      return 'EScooter';
    case 'WALKER':
      return 'Walking';
    case 'BICYCLE_CARGO':
      return 'Bicycle cargo';
    case 'EBICYCLE_CARGO':
      return 'EBicycle cargo';
    case 'MONOWHEEL':
      return 'Monowheel';
    case 'SIDEWALK':
      return 'Sidewalk';
    case 'UNRECOGNIZED':
      return 'Unrecognized';
  }
};

export const getUpdatedAtTooltip = ({
  updatedAt,
  isTimeRangeForToday,
  prefix,
}: {
  updatedAt?: Date;
  isTimeRangeForToday?: boolean;
  prefix?: string;
}) => {
  if (!updatedAt) {
    return;
  }

  const prefixText = prefix ? `${prefix}: ` : '';
  if (isTimeRangeForToday) {
    return `${prefixText}updated ${formatRelative(updatedAt, new Date())}`;
  }
  return `${prefixText}last updated: ${formatDate(updatedAt, 'yyyy-MM-dd HH:SS')}`;
};

export const toUnifiedCourierId = (num: CourierId | number): UnifiedCourierId =>
  UnifiedCourierId.parse(num.toString());

export const toLegacyCourierId = (val: string | number): CourierId =>
  CourierId.parse(typeof val === 'string' ? Number.parseInt(val, 10) : val);

export const getCourierName = (courier?: { preferredName?: string }): string | undefined =>
  courier?.preferredName;

export const getCourierShiftStatus = (value?: CourierStatus) => {
  if (value === CourierStatus.enum.unspecified) {
    return 'Unspecified';
  }
  if (value === CourierStatus.enum.noactiveschedule) {
    return 'No active shift';
  }
  if (value === CourierStatus.enum.hasupcomingschedule) {
    return 'Has upcoming shift';
  }
  if (value === CourierStatus.enum.hasnotstartedschedule) {
    return `Hasn't started the shift`;
  }
  if (value === CourierStatus.enum.onschedule) {
    return 'On shift';
  }
  return '';
};
